import React, { useState } from 'react';
import axios from 'axios';
import { Download, FileText, FileSpreadsheet, Presentation, Award, Loader2, Wand2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useLang } from '@/context/LangContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import ExportProjectPreview from '@/components/app/ExportProjectPreview';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KINDS = [
    { value: 'achievement', fr: 'Achèvement des travaux', en: 'Works completion' },
    { value: 'reception', fr: 'Réception', en: 'Reception' },
    { value: 'payment', fr: 'Paiement', en: 'Payment' },
    { value: 'closure', fr: 'Clôture', en: 'Closure' },
];

export default function ProjectExports({ project, workspaceId }) {
    const { session } = useAuth();
    const { lang, t } = useLang();
    const [busy, setBusy] = useState(null);
    const [openAtt, setOpenAtt] = useState(false);
    const [attForm, setAttForm] = useState({ kind: 'achievement', signer_name: '', extra_text: '' });
    // Pre-export edit dialog (V16.6 — admin/elevated can adjust title + intro before generating the PDF)
    const [openPreview, setOpenPreview] = useState(false);
    const [preForm, setPreForm] = useState({ title_override: '', intro_override: '' });

    const download = async (urlPath, filename, params = {}) => {
        if (!session?.access_token) return;
        setBusy(urlPath);
        try {
            const r = await axios.get(`${API}${urlPath}`, {
                headers: { Authorization: `Bearer ${session.access_token}` },
                responseType: 'blob',
                params: { workspace_id: workspaceId, ...params },
            });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
            URL.revokeObjectURL(url);
            toast.success('Export généré');
        } catch (e) {
            toast.error(e.response?.data?.detail || e.message);
        }
        setBusy(null);
    };

    const generateSynthesisWithOverrides = async () => {
        await download(
            `/exports/${project.id}/synthesis.pdf`,
            `synthese_${project.name.slice(0, 20)}.pdf`,
            {
                title_override: preForm.title_override.trim() || undefined,
                intro_override: preForm.intro_override.trim() || undefined,
            }
        );
        setOpenPreview(false);
        setPreForm({ title_override: '', intro_override: '' });
    };

    const generateAttestation = async (e) => {
        e.preventDefault();
        setBusy('attestation');
        try {
            const r = await axios.post(`${API}/attestations/generate`, {
                project_id: project.id,
                workspace_id: workspaceId,
                kind: attForm.kind,
                signer_name: attForm.signer_name,
                extra_text: attForm.extra_text,
            }, {
                headers: { Authorization: `Bearer ${session.access_token}` },
                responseType: 'blob',
            });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement('a'); a.href = url; a.download = `attestation_${project.name.slice(0, 20)}.pdf`; a.click();
            URL.revokeObjectURL(url);
            setOpenAtt(false);
        } catch (e) {
            alert(e.response?.data?.detail || e.message);
        }
        setBusy(null);
    };

    const items = [
        {
            icon: FileText,
            label: t('exports.synthesis'),
            sub: t('exports.synthesis_sub'),
            path: `/exports/${project.id}/synthesis.pdf`,
            file: `synthese_${project.name.slice(0, 20)}.pdf`,
            testid: 'export-synthesis',
        },
        {
            icon: FileSpreadsheet,
            label: t('exports.budget'),
            sub: t('exports.budget_sub'),
            path: `/exports/${project.id}/budget.xlsx`,
            file: `budget_${project.name.slice(0, 20)}.xlsx`,
            testid: 'export-budget',
        },
        {
            icon: Presentation,
            label: t('exports.comex'),
            sub: t('exports.comex_sub'),
            path: `/exports/${project.id}/comex.pptx`,
            file: `comex_${project.name.slice(0, 20)}.pptx`,
            testid: 'export-comex',
        },
    ];

    return (
        <div className="space-y-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <div className="eyebrow mb-2">{t('exports.eyebrow')}</div>
                    <h2 className="font-heading text-2xl">{t('exports.title')}</h2>
                    <p className="text-foreground/70 mt-2">{t('exports.sub')}</p>
                </div>
                <ExportProjectPreview projectId={project.id} projectName={project.name} />
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {items.map(it => (
                    <div key={it.path} className="card-soft p-6 text-left hover:border-primary/40 transition-all group">
                        <div className="h-10 w-10 rounded-lg bg-primary-light text-primary flex items-center justify-center mb-4 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                            <it.icon size={18} strokeWidth={1.6} />
                        </div>
                        <div className="font-heading text-xl mb-1">{it.label}</div>
                        <p className="text-sm text-foreground/70 mb-4">{it.sub}</p>
                        <div className="flex flex-wrap gap-2">
                            <Button
                                onClick={() => download(it.path, it.file)}
                                disabled={busy === it.path}
                                size="sm" className="bg-primary hover:bg-primary-hover"
                                data-testid={it.testid}
                            >
                                {busy === it.path ? <Loader2 size={14} className="animate-spin mr-2" /> : <Download size={14} className="mr-2" />}
                                {t('exports.download')}
                            </Button>
                            {it.testid === 'export-synthesis' && (
                                <Button
                                    onClick={() => setOpenPreview(true)}
                                    size="sm" variant="outline"
                                    data-testid="export-synthesis-customize"
                                    title="Modifier titre & introduction avant export"
                                >
                                    <Wand2 size={13} className="mr-1.5" /> Personnaliser
                                </Button>
                            )}
                        </div>
                    </div>
                ))}
                <button
                    onClick={() => setOpenAtt(true)}
                    className="card-soft p-6 text-left hover:border-secondary/40 transition-all group"
                    data-testid="export-attestation-btn"
                >
                    <div className="h-10 w-10 rounded-lg bg-secondary-light text-secondary flex items-center justify-center mb-4 group-hover:bg-secondary group-hover:text-secondary-foreground transition-colors">
                        <Award size={18} strokeWidth={1.6} />
                    </div>
                    <div className="font-heading text-xl mb-1">{t('exports.attestation')}</div>
                    <p className="text-sm text-foreground/70 mb-4">{t('exports.attestation_sub')}</p>
                    <div className="inline-flex items-center text-sm text-secondary font-medium">
                        <Award size={14} className="mr-2" /> {t('exports.attestation_cta')}
                    </div>
                </button>
            </div>

            <Dialog open={openAtt} onOpenChange={setOpenAtt}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="font-heading text-2xl">{t('exports.attestation')}</DialogTitle></DialogHeader>
                    <form onSubmit={generateAttestation} className="space-y-4" data-testid="attestation-form">
                        <div className="space-y-2"><Label>{t('exports.attestation_kind')}</Label>
                            <select value={attForm.kind} onChange={(e) => setAttForm({ ...attForm, kind: e.target.value })} className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" data-testid="attestation-kind">
                                {KINDS.map(k => <option key={k.value} value={k.value}>{lang === 'fr' ? k.fr : k.en}</option>)}
                            </select>
                        </div>
                        <div className="space-y-2"><Label>{t('exports.attestation_signer')}</Label><Input value={attForm.signer_name} onChange={(e) => setAttForm({ ...attForm, signer_name: e.target.value })} placeholder="Nom & fonction" data-testid="attestation-signer" /></div>
                        <div className="space-y-2"><Label>{t('exports.attestation_extra')}</Label><Textarea rows={3} value={attForm.extra_text} onChange={(e) => setAttForm({ ...attForm, extra_text: e.target.value })} placeholder={t('exports.attestation_extra_ph')} data-testid="attestation-extra" /></div>
                        <DialogFooter>
                            <Button type="button" variant="ghost" onClick={() => setOpenAtt(false)}>{t('common.cancel')}</Button>
                            <Button type="submit" disabled={busy === 'attestation'} className="bg-primary hover:bg-primary-hover" data-testid="attestation-generate">
                                {busy === 'attestation' ? <Loader2 size={14} className="animate-spin" /> : t('exports.attestation_cta')}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog open={openPreview} onOpenChange={setOpenPreview}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl">Personnaliser la synthèse</DialogTitle>
                        <DialogDescription>
                            Ajustez le titre et l'introduction qui apparaîtront en première page du PDF. Laissez vide pour conserver les valeurs par défaut du projet.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="space-y-2">
                            <Label>Titre principal</Label>
                            <Input
                                value={preForm.title_override}
                                onChange={(e) => setPreForm({ ...preForm, title_override: e.target.value })}
                                placeholder={project.name}
                                data-testid="pre-export-title"
                                maxLength={120}
                            />
                            <p className="text-xs text-muted-foreground">Vide → "{project.name}" sera utilisé.</p>
                        </div>
                        <div className="space-y-2">
                            <Label>Introduction exécutive</Label>
                            <Textarea
                                rows={4}
                                value={preForm.intro_override}
                                onChange={(e) => setPreForm({ ...preForm, intro_override: e.target.value })}
                                placeholder="Ex. : Note de synthèse à l'attention du Comité de pilotage du 15 juin 2026."
                                data-testid="pre-export-intro"
                                maxLength={500}
                            />
                            <p className="text-xs text-muted-foreground">Une ligne d'accroche pour donner du contexte à votre lecteur.</p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setOpenPreview(false)}>Annuler</Button>
                        <Button
                            onClick={generateSynthesisWithOverrides}
                            disabled={busy?.includes('synthesis')}
                            className="bg-primary hover:bg-primary-hover"
                            data-testid="pre-export-generate"
                        >
                            {busy?.includes('synthesis') ? <Loader2 size={14} className="animate-spin mr-2" /> : <Download size={14} className="mr-2" />}
                            Générer le PDF
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
